import React, { useState, useEffect, useRef } from 'react';
import { Send, FileText, MessageSquare, Hash, Phone, AlertCircle, CheckCircle, RotateCcw, ClipboardPaste, X, Copy, Info, ChevronDown, ChevronUp, Eye, Zap, Shield } from 'lucide-react';
import api from '../services/api';
import SearchableDropdown from '../components/common/SearchableDropdown';

/**
 * PROMPT-042: Send SMS Page
 * 
 * Features:
 * - Header selection (user-mapped only)
 * - Template selection (header-mapped only)
 * - Read-only original template display
 * - Editable message with dynamic variables {#var#}
 * - Runtime variable replacement
 * - GSM/Unicode encoding support
 * - Character counting
 * - Indian mobile number validation
 * - Save to database in PENDING state
 */

// DLT approved variable tags with descriptions
const DLT_VARIABLE_TAGS = [
  { tag: '{#var#}', label: 'General Purpose', description: 'Any text value', color: '#d97706' },
  { tag: '{#numeric#}', label: 'Numeric', description: 'OTPs, amounts, numbers', color: '#dc2626' },
  { tag: '{#alphanumeric#}', label: 'Alphanumeric', description: 'Reference IDs, ticket numbers', color: '#7c3aed' },
  { tag: '{#url#}', label: 'URL', description: 'Website links', color: '#2563eb' },
  { tag: '{#urlott#}', label: 'URL OTT', description: 'OTT / App download links', color: '#0891b2' },
  { tag: '{#cbn#}', label: 'Callback Number', description: 'Callback numbers', color: '#059669' },
  { tag: '{#email#}', label: 'Email', description: 'Email addresses', color: '#db2777' },
];

// Regex to match all DLT variable tags
const DLT_VAR_REGEX = /\{#(?:var|numeric|alphanumeric|url|urlott|cbn|email)#\}/g;

const SendSMS = () => {
  // State management
  const [headers, setHeaders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedHeader, setSelectedHeader] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateType, setTemplateType] = useState('');
  const [originalTemplate, setOriginalTemplate] = useState('');
  const [editableMessage, setEditableMessage] = useState('');
  const [encodingType, setEncodingType] = useState('GSM');
  const [variables, setVariables] = useState([]); // Now stores objects: { tag, index }
  const [variableValues, setVariableValues] = useState([]);
  const [mobileNumbers, setMobileNumbers] = useState('');
  const [validNumbers, setValidNumbers] = useState([]);
  const [invalidNumbers, setInvalidNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingHeaders, setLoadingHeaders] = useState(false);
  const [headersError, setHeadersError] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showVariables, setShowVariables] = useState(true);
  const textareaRef = useRef(null);
  const overlayRef = useRef(null);
  const [pasteModal, setPasteModal] = useState({ open: false, text: '', error: '' });
  const [pasteToast, setPasteToast] = useState({ show: false, type: '', message: '' });

  // Load headers on mount
  useEffect(() => {
    loadHeaders();
  }, []);


  // Auto-detect encoding based on message content
  useEffect(() => {
    if (!editableMessage) return;
    // GSM 03.38 basic character set (simplified check)
    // If any character is outside GSM-7, switch to UCS-2
    const gsmChars = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1BÆæßÉ !"#¤%&'()*+,\-.\/0-9:;<=>?¡A-ZÄÖÑÜa-zäöñüà§^{}\\\[~\]|€\s]*$/;
    const isGSM = gsmChars.test(editableMessage);
    setEncodingType(isGSM ? 'GSM' : 'UCS-2');
  }, [editableMessage]);

  // Variable detection is now done only when template is loaded
  // This prevents re-detection when user types in variable inputs

  // Load user-mapped headers
  const loadHeaders = async () => {
    setLoadingHeaders(true);
    setHeadersError('');
    try {
      const response = await api.get('/user/headers');
      console.log('[SendSMS] Headers response:', response.data);
      
      if (response.data.success && response.data.headers) {
        setHeaders(response.data.headers);
        if (response.data.headers.length === 0) {
          setHeadersError('No headers are mapped to your account. Please contact admin.');
        }
      } else {
        setHeaders([]);
        setHeadersError('No headers available');
      }
    } catch (err) {
      console.error('[SendSMS] Failed to load headers:', err);
      const errorMsg = err.response?.data?.error || 'Unable to fetch headers. Please contact admin.';
      setHeadersError(errorMsg);
      setHeaders([]);
    } finally {
      setLoadingHeaders(false);
    }
  };

  // Load templates for selected header
  const loadTemplates = async (headerId) => {
    if (!headerId) {
      setTemplates([]);
      return;
    }

    setLoadingTemplates(true);
    try {
      const response = await api.get(`/user/headers/${headerId}/templates`);
      console.log('[SendSMS] Templates response:', response.data);
      
      if (response.data.success && response.data.templates) {
        setTemplates(response.data.templates);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      console.error('[SendSMS] Failed to load templates:', err);
      setError(err.response?.data?.error || 'Failed to load templates');
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Handle header selection
  const handleHeaderChange = (e) => {
    const headerId = e.target.value;
    setSelectedHeader(headerId.toString());
    setSelectedTemplate('');
    setTemplateId('');
    setTemplateName('');
    setTemplateType('');
    setOriginalTemplate('');
    setEditableMessage('');
    setTemplates([]);
    setVariables([]);
    setVariableValues([]);
    setPasteModal({ open: false, text: '', error: '' });
    
    if (headerId) {
      loadTemplates(headerId);
    }
  };

  // Handle template selection
  const handleTemplateChange = async (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);
    
    if (!templateId) {
      setTemplateId('');
      setTemplateName('');
      setTemplateType('');
      setOriginalTemplate('');
      setEditableMessage('');
      setVariables([]);
      setVariableValues([]);
      setPasteModal({ open: false, text: '', error: '' });
      return;
    }

    try {
      const response = await api.get(`/user/templates/${templateId}`);
      console.log('[SendSMS] Template content response:', response.data);
      
      if (response.data.success && response.data.template) {
        const content = response.data.template.template_content;
        const dltTemplateId = response.data.template.template_id;
        setTemplateId(dltTemplateId);
        setTemplateName(response.data.template.template_name || '');
        setTemplateType(response.data.template.template_type || '');
        setOriginalTemplate(content);
        setEditableMessage(content);
        
        // Load template content into the Type your Message panel for direct editing
        setPasteModal({ open: false, text: content, error: '' });
        
        // Detect all DLT variable tags from originalTemplate (source of truth)
        const matches = content.match(DLT_VAR_REGEX) || [];
        const count = matches.length;
        
        // Store variable objects with tag type info
        const varObjects = matches.map((tag, idx) => {
          const tagInfo = DLT_VARIABLE_TAGS.find(t => t.tag === tag) || DLT_VARIABLE_TAGS[0];
          return { tag, index: idx, ...tagInfo };
        });
        setVariables(varObjects);
        setVariableValues(Array(count).fill(''));
        
        // Set default visibility: collapsed if more than 3 variables
        setShowVariables(count <= 3);
      }
    } catch (err) {
      console.error('[SendSMS] Failed to load template content:', err);
      setError(err.response?.data?.error || 'Failed to load template content');
    }
  };

  // Generate preview message with variable replacements
  // This function creates a preview WITHOUT modifying the original template
  const generatePreviewMessage = (values) => {
    let previewMessage = originalTemplate;
    let varIndex = 0;
    
    previewMessage = previewMessage.replace(DLT_VAR_REGEX, (match) => {
      const replacement = values[varIndex] || match;
      varIndex++;
      return replacement;
    });
    
    return previewMessage;
  };

  // Handle variable value change with runtime replacement
  const handleVariableChange = (index, value) => {
    // Validate max length
    if (value.length > 30) {
      return;
    }

    // Update variable values in separate state
    const newValues = [...variableValues];
    newValues[index] = value;
    setVariableValues(newValues);

    // Generate preview message from originalTemplate (source of truth)
    // This ensures variable detection is NEVER affected by user input
    const previewMessage = generatePreviewMessage(newValues);
    setEditableMessage(previewMessage);
  };

  // Reset editable message to original template
  const handleResetToOriginal = () => {
    // Reset editable message to original template
    setEditableMessage(originalTemplate);
    
    // Clear all variable input values
    const emptyValues = Array(variables.length).fill('');
    setVariableValues(emptyValues);
    
    // Clear paste message panel
    setPasteModal({ open: false, text: '', error: '' });
    
    // Character count will automatically recalculate based on originalTemplate
    // Variable count is preserved (no re-detection)
  };

  // Shared: extract variable values from pasted text by matching against original template
  // Returns { success: true, values: [...] } or { success: false, error: '...' }
  const extractVariablesFromText = (pastedText) => {
    if (!pastedText || !pastedText.trim()) {
      return { success: false, error: 'No text to process' };
    }
    const text = pastedText.trim();

    if (!originalTemplate) {
      return { success: false, error: 'Please select a template first' };
    }

    const parts = originalTemplate.split(DLT_VAR_REGEX);
    const varCount = parts.length - 1;

    if (varCount === 0) {
      // Static template — check if pasted text matches
      if (text === originalTemplate.trim()) {
        return { success: true, values: [], static: true };
      }
      return { success: false, error: 'This is a static template with no variables' };
    }

    // Build regex: escape each fixed part, capture variable positions
    const regexParts = parts.map(part =>
      part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    const regexStr = '^' + regexParts.join('(.+?)') + '$';

    let regex;
    try {
      regex = new RegExp(regexStr, 's');
    } catch (e) {
      return { success: false, error: 'Template pattern error. Please fill variables manually.' };
    }

    const match = text.match(regex);
    if (!match) {
      return { success: false, error: 'Message does not match template pattern. Fixed parts must match exactly.' };
    }

    const extractedValues = [];
    const tooLong = [];
    for (let i = 1; i <= varCount; i++) {
      const val = match[i] || '';
      extractedValues.push(val);
      if (val.length > 30) {
        tooLong.push({ index: i, length: val.length, value: val.substring(0, 40) + (val.length > 40 ? '...' : '') });
      }
    }

    if (tooLong.length > 0) {
      const details = tooLong.map(v => `Var ${v.index}: ${v.length} chars`).join(', ');
      return { success: false, error: `Variable values exceed 30 char limit (${details})` };
    }

    return { success: true, values: extractedValues };
  };

  // Apply extracted variable values to the form
  const applyExtractedValues = (values) => {
    setVariableValues(values);
    const previewMessage = generatePreviewMessage(values);
    setEditableMessage(previewMessage);
  };

  // Handle paste message from sidebar panel
  const handlePasteApply = () => {
    const result = extractVariablesFromText(pasteModal.text);
    if (result.success) {
      if (result.values.length > 0) {
        applyExtractedValues(result.values);
      }
      // Keep the text in the textarea, only clear the error
      setPasteModal(prev => ({ ...prev, error: '' }));
    } else {
      setPasteModal(prev => ({ ...prev, error: result.error }));
    }
  };

  // Handle direct paste on Message Preview area (4th method)
  const handleDirectPaste = (e) => {
    // Only process if we have variables
    if (!originalTemplate || variables.length === 0) return;

    const pastedText = e.clipboardData?.getData('text');
    if (!pastedText) return;

    // Check if the paste happened inside a var input — if so, let it through normally
    if (e.target.tagName === 'INPUT') return;

    e.preventDefault();

    const result = extractVariablesFromText(pastedText);
    if (result.success && result.values.length > 0) {
      applyExtractedValues(result.values);
      setPasteToast({ show: true, type: 'success', message: `${result.values.length} variable${result.values.length > 1 ? 's' : ''} extracted successfully` });
    } else {
      setPasteToast({ show: true, type: 'error', message: result.error || 'Could not extract variables from pasted text' });
    }
    setTimeout(() => setPasteToast({ show: false, type: '', message: '' }), 3000);
  };

  // Copy editable message to clipboard
  const handleCopyMessage = () => {
    if (editableMessage) {
      navigator.clipboard.writeText(editableMessage).then(() => {
        // Brief visual feedback could be added here
      }).catch(() => {});
    }
  };

  // Calculate character count and SMS count
  const calculateCharacterStats = () => {
    const length = editableMessage.length;
    const isUnicode = encodingType === 'UCS-2';
    
    let smsCount = 1;
    if (isUnicode) {
      // Unicode: 70 chars per SMS, 67 for concatenated
      smsCount = length <= 70 ? 1 : Math.ceil(length / 67);
    } else {
      // GSM-7: 160 chars per SMS, 153 for concatenated
      smsCount = length <= 160 ? 1 : Math.ceil(length / 153);
    }

    return { length, smsCount };
  };

  // Process mobile numbers with real-time deduplication and validation
  const processMobileNumbers = (numbersText) => {
    const numbers = numbersText
      .split(/[\n,]+/)
      .map(n => n.trim())
      .filter(n => n.length > 0);

    // Updated regex to only accept numbers (0-9), no alpha characters
    const numericRegex = /^[0-9]+$/;
    const indianMobileRegex = /^(\+91|91)?[6-9]\d{9}$/;
    const validSet = new Set();
    const invalid = [];

    numbers.forEach(num => {
      const cleaned = num.replace(/[\s-]/g, '');
      
      // First check if it's numeric only
      if (!numericRegex.test(cleaned.replace(/^\+91|^91/, ''))) {
        // Contains non-numeric characters
        if (!invalid.includes(num)) {
          invalid.push(num);
        }
      } else if (indianMobileRegex.test(cleaned)) {
        // Valid Indian mobile number
        let normalized = cleaned;
        if (normalized.startsWith('+91')) {
          normalized = normalized.substring(3);
        } else if (normalized.startsWith('91')) {
          normalized = normalized.substring(2);
        }
        // Add to set to automatically deduplicate
        validSet.add(normalized);
      } else {
        // Invalid format but numeric only
        if (!invalid.includes(num)) {
          invalid.push(num);
        }
      }
    });

    const valid = Array.from(validSet);
    return { valid, invalid };
  };

  // Validate mobile numbers (kept for backward compatibility)
  const validateMobileNumbers = (numbersText) => {
    return processMobileNumbers(numbersText);
  };

  // Handle mobile numbers input change with real-time processing
  const handleMobileNumbersChange = (e) => {
    const inputText = e.target.value;
    setMobileNumbers(inputText);
    
    // Process numbers in real-time
    const { valid, invalid } = processMobileNumbers(inputText);
    setValidNumbers(valid);
    setInvalidNumbers(invalid);
    
    // Clear validation error if user is typing
    if (validationErrors.mobile) {
      setValidationErrors(prev => ({ ...prev, mobile: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!selectedHeader) {
      errors.header = 'Please select a header';
    }

    if (!selectedTemplate) {
      errors.template = 'Please select a template';
    }

    if (!editableMessage.trim()) {
      errors.message = 'Message cannot be empty';
    }

    // Check if all variables are filled
    const hasUnfilledVars = DLT_VAR_REGEX.test(editableMessage);
    const hasEmptyVariables = hasUnfilledVars && variableValues.some((v) => !v || v.trim() === '');

    if (hasEmptyVariables) {
      errors.variables = 'Please fill all variable values';
    }

    if (!mobileNumbers.trim()) {
      errors.mobile = 'Please enter at least one mobile number';
    } else if (validNumbers.length === 0) {
      errors.mobile = 'No valid mobile numbers found';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitResult(null);
    setResponseTime(null);

    if (!validateForm()) {
      return;
    }

    // Use pre-processed valid numbers
    const numbersToSend = validNumbers;

    setLoading(true);
    const startTime = performance.now();

    try {
      // Get entity_id from selected header
      const selectedHeaderObj = headers.find(h => h.id === parseInt(selectedHeader));
      const entityId = selectedHeaderObj?.entity_id || '';
      
      const response = await api.post('/user/send-sms', {
        to: numbersToSend.join(','), // All unique valid numbers
        message: editableMessage,
        entity_id: entityId, // DLT Entity ID
        header_id: selectedHeaderObj?.header_name || '', // DLT Header ID (string)
        template_id: templateId, // DLT Template ID (string)
        encoding_type: encodingType,
        message_type: 'transactional',
        channel: 'GUI',
      });

      const elapsed = Math.round(performance.now() - startTime);
      setResponseTime(elapsed);
      
      console.log('[SendSMS] Submit response:', response.data, `(${elapsed}ms)`);

      // Check if the response contains valid data (PROMPT-277 response format)
      if (response.data.status !== 'accepted') {
        setError(response.data?.message || 'Failed to submit SMS. Please try again.');
        setLoading(false);
        return;
      }

      // PHASE-8: Store full result for rich display
      setSubmitResult(response.data);
      const count = response.data.count || response.data.message_ids?.length || 1;
      setSuccess(`SMS accepted for processing — ${count} number${count > 1 ? 's' : ''} in ${elapsed}ms`);

      // Reset form
      setSelectedTemplate('');
      setOriginalTemplate('');
      setEditableMessage('');
      setVariables([]);
      setVariableValues([]);
      setMobileNumbers('');
      setValidNumbers([]);
      setInvalidNumbers([]);
      setValidationErrors({});
    } catch (err) {
      const elapsed = Math.round(performance.now() - startTime);
      console.error('[SendSMS] Submit error:', err, `(${elapsed}ms)`);
      const errData = err.response?.data;
      const statusCode = err.response?.status;

      // PHASE-8: Handle 429 rate limit error
      if (statusCode === 429) {
        setError(`Rate limit exceeded. Your account allows a maximum number of requests per second. Please wait a moment and try again.`);
      } else {
        const msg = errData?.message || 'Unable to send SMS.';
        const refId = errData?.reference_id || '';
        if (refId) {
          setError(`${msg}\nReference No: ${refId}\nPlease contact support with this reference number.`);
        } else {
          setError(msg);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const stats = calculateCharacterStats();

  const cardBase = "bg-white rounded-xl transition-all duration-200";
  const cardBorder = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

  // Validation checklist items
  const validationChecks = [
    { label: 'Header selected', done: !!selectedHeader, key: 'header' },
    { label: 'Template selected', done: !!selectedTemplate, key: 'template' },
    { label: 'Message composed', done: !!editableMessage && editableMessage.trim().length > 0, key: 'message' },
    ...(variables.length > 0 ? [{ label: `Variables filled (${variableValues.filter(v => v && v.trim()).length}/${variables.length})`, done: variableValues.filter(v => v && v.trim()).length === variables.length, key: 'vars' }] : []),
    { label: 'No unfilled variables', done: !!editableMessage && !DLT_VAR_REGEX.test(editableMessage), key: 'novar' },
    { label: 'Recipients added', done: validNumbers.length > 0, key: 'recipients' },
  ];
  const readyCount = validationChecks.filter(c => c.done).length;
  const allReady = readyCount === validationChecks.length;

  // Selected header/template objects for sidebar display
  const selectedHeaderObj = headers.find(h => h.id === parseInt(selectedHeader));
  const selectedTemplateObj = templates.find(t => t.id === parseInt(selectedTemplate));

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Send className="w-5 h-5 text-white" />
            </div>
            Send SMS
          </h1>
          <p className="text-sm text-gray-500 mt-1">Compose and send messages with DLT-approved templates</p>
        </div>
      </div>

      {/* PHASE-8: Rich Success Result Card */}
      {success && submitResult && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(16, 185, 129, 0.2)' }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-white" />
              <span className="text-sm font-semibold text-white">SMS Submitted Successfully</span>
            </div>
            <div className="flex items-center gap-3">
              {responseTime !== null && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                  <Zap className="w-3 h-3" /> {responseTime}ms
                </span>
              )}
              <button onClick={() => { setSuccess(''); setSubmitResult(null); }} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3" style={{ background: 'rgba(16, 185, 129, 0.03)' }}>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Count</p>
              <p className="text-lg font-bold text-gray-900">{submitResult.count || 0}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Campaign ID</p>
              <p className="text-xs font-mono text-gray-700 mt-1 truncate" title={submitResult.campaign_id}>{submitResult.campaign_id || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Reference ID</p>
              <div className="flex items-center gap-1 mt-1">
                <p className="text-xs font-mono text-gray-700 truncate" title={submitResult.reference_id}>{submitResult.reference_id || 'N/A'}</p>
                {submitResult.reference_id && (
                  <button onClick={() => navigator.clipboard.writeText(submitResult.reference_id)} className="text-gray-400 hover:text-indigo-500 flex-shrink-0" title="Copy"><Copy className="w-3 h-3" /></button>
                )}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Response Time</p>
              <p className="text-xs font-mono mt-1" style={{ color: responseTime < 50 ? '#059669' : responseTime < 200 ? '#d97706' : '#dc2626' }}>
                {responseTime !== null ? `${responseTime}ms` : 'N/A'}
              </p>
            </div>
          </div>
          {submitResult.message_ids && submitResult.message_ids.length > 0 && (
            <div className="px-4 py-2 flex items-center gap-2 flex-wrap" style={{ background: 'rgba(16, 185, 129, 0.02)', borderTop: '1px solid rgba(16, 185, 129, 0.1)' }}>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Message IDs:</span>
              {submitResult.message_ids.map((id, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded text-[11px] font-mono" style={{ background: 'rgba(99, 102, 241, 0.06)', color: '#4f46e5' }}>{id}</span>
              ))}
              <button onClick={() => navigator.clipboard.writeText(submitResult.message_ids.join(', '))} className="text-gray-400 hover:text-indigo-500 ml-1" title="Copy all"><Copy className="w-3 h-3" /></button>
            </div>
          )}
        </div>
      )}
      {/* Plain success fallback (no submitResult) */}
      {success && !submitResult && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-green-800">SMS Submitted Successfully</p>
            <p className="text-sm text-green-700 mt-1 break-all">{success}</p>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-400 hover:text-green-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}
      {error && error.includes('Rate limit') ? (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)' }}>
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-amber-800">Rate Limit Exceeded</p>
            <p className="text-sm text-amber-700 mt-1">{error}</p>
            <p className="text-xs text-amber-500 mt-2">Your TPS limit can be viewed in Settings. Contact support to increase it.</p>
          </div>
          <button onClick={() => setError('')} className="text-amber-400 hover:text-amber-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
      ) : error ? (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>
            <AlertCircle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-red-800">Submission Failed</p>
            <p className="text-sm text-red-700 mt-1 whitespace-pre-line">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
      ) : null}

      {/* ═══════════ 75 / 25 SPLIT LAYOUT ═══════════ */}
      <div className="flex gap-5 items-start">

        {/* ──────── LEFT SIDE (75%) — Main Form ──────── */}
        <form onSubmit={handleSubmit} className="flex-1 min-w-0 space-y-4" style={{ flexBasis: '75%' }}>

          {/* Header + Template — Side by Side */}
          <div className={cardBase + " p-5"} style={cardBorder}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Header (Sender ID) */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                  <FileText className="w-3 h-3" style={{ color: '#6366f1' }} />
                  Header (Sender ID)
                </label>
                {loadingHeaders ? (
                  <div className="flex items-center gap-2 py-2.5 px-3 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-200" style={{ borderTopColor: '#6366f1' }}></div>
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : headersError ? (
                  <div className="p-2.5 rounded-lg flex items-start gap-2" style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                    <AlertCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-amber-700">{headersError}</span>
                  </div>
                ) : (
                  <>
                    <SearchableDropdown
                      options={headers.map((header) => ({
                        value: header.id.toString(),
                        label: `${header.header_name} (${header.header_type})`
                      }))}
                      value={selectedHeader}
                      onChange={(value) => handleHeaderChange({ target: { value } })}
                      placeholder="-- Select Header --"
                      clearable={false}
                      className={validationErrors.header ? 'border-red-300' : ''}
                    />
                    {validationErrors.header && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.header}</p>
                    )}
                  </>
                )}
              </div>

              {/* Template */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                  <MessageSquare className="w-3 h-3" style={{ color: '#6366f1' }} />
                  Template
                </label>
                {loadingTemplates ? (
                  <div className="flex items-center gap-2 py-2.5 px-3 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-200" style={{ borderTopColor: '#6366f1' }}></div>
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : (
                  <>
                    <SearchableDropdown
                      options={templates.map((template) => ({
                        value: template.id.toString(),
                        label: `${template.template_name} (${template.template_id})`
                      }))}
                      value={selectedTemplate}
                      onChange={(value) => handleTemplateChange({ target: { value } })}
                      placeholder="-- Select Template --"
                      clearable={false}
                      className={validationErrors.template ? 'border-red-300' : ''}
                    />
                    {validationErrors.template && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.template}</p>
                    )}
                    {templates.length === 0 && selectedHeader && !loadingTemplates && (
                      <p className="text-gray-400 text-xs mt-1">No templates for this header</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Template View — Side by Side */}
          {originalTemplate && (
            <div className={cardBase + " p-5"} style={cardBorder}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Type your Message — Editable Panel */}
                <div className="flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: 'rgba(99, 102, 241, 0.1)' }}>
                      <ClipboardPaste className="w-3 h-3" style={{ color: '#6366f1' }} />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-700">Type your Message</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Edit the message directly or paste a complete message to auto-fill variables.</p>
                  <textarea
                    value={pasteModal.text}
                    onChange={(e) => setPasteModal(prev => ({ ...prev, text: e.target.value, error: '' }))}
                    onPaste={(e) => { setTimeout(() => setPasteModal(prev => ({ ...prev, error: '' })), 0); }}
                    rows={5}
                    className="w-full px-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none leading-relaxed"
                    style={{ border: '1px solid rgba(99, 102, 241, 0.12)', minHeight: '140px', maxHeight: '140px' }}
                    placeholder="Type or paste your message here..." />
                  {pasteModal.error && (
                    <div className="mt-1.5 flex items-start gap-1.5 p-2 rounded" style={{ background: 'rgba(239, 68, 68, 0.06)' }}>
                      <AlertCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-red-600">{pasteModal.error}</p>
                    </div>
                  )}
                  <button type="button" onClick={handlePasteApply}
                    className="mt-2 w-full py-2 rounded-lg text-xs font-semibold text-white flex items-center justify-center gap-1.5 transition-all hover:shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                    <ClipboardPaste className="w-3 h-3" />Apply Message
                  </button>
                </div>

                {/* Editable Message */}
                <div className="flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                        <MessageSquare className="w-3 h-3" style={{ color: '#059669' }} />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-700">Message Preview</h3>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button type="button" onClick={handleCopyMessage} className="px-2.5 py-1 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition-all flex items-center gap-1" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }} title="Copy message to clipboard"><Copy className="w-3 h-3" />Copy</button>
                      <button type="button" onClick={handleResetToOriginal} className="px-2.5 py-1 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition-all flex items-center gap-1" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }} title="Reset to original template"><RotateCcw className="w-3 h-3" />Reset</button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Live preview of your final message with variables filled in.</p>
                  <div className="relative flex-1">
                    {/* Inline editable message — static text is read-only, {#var#} slots are editable inputs */}
                    {/* Supports direct paste (Ctrl+V) on the preview area to extract variables */}
                    <div
                      ref={overlayRef}
                      onPaste={handleDirectPaste}
                      className="px-3 py-2.5 rounded-lg overflow-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700 select-text"
                      style={{ minHeight: '140px', maxHeight: '200px', border: validationErrors.message ? '1px solid #fca5a5' : '1px solid rgba(16, 185, 129, 0.2)' }}>
                      {(() => {
                        let result = [];
                        let textParts = originalTemplate.split(DLT_VAR_REGEX);
                        let tagMatches = originalTemplate.match(DLT_VAR_REGEX) || [];
                        let vi = 0;
                        textParts.forEach((part, index) => {
                          if (part) result.push(<span key={`text-${index}`}>{part}</span>);
                          if (index < tagMatches.length) {
                            const currentVi = vi;
                            const val = variableValues[currentVi] || '';
                            const varInfo = variables[currentVi] || {};
                            const tagColor = varInfo.color || '#d97706';
                            result.push(
                              <span key={`var-wrap-${index}`} className="inline-flex items-center mx-0.5" style={{ verticalAlign: 'baseline' }}>
                                <span className="flex items-center rounded-md overflow-hidden" style={{
                                  border: val ? '1.5px solid rgba(16, 185, 129, 0.5)' : `1.5px dashed ${tagColor}60`,
                                  background: val ? 'rgba(16, 185, 129, 0.06)' : `${tagColor}08`,
                                }}>
                                  <span className="px-1.5 py-0.5 text-[10px] font-bold select-none" style={{
                                    background: val ? 'rgba(16, 185, 129, 0.15)' : `${tagColor}18`,
                                    color: val ? '#059669' : tagColor,
                                    borderRight: val ? '1px solid rgba(16, 185, 129, 0.2)' : `1px solid ${tagColor}25`,
                                  }}>{currentVi + 1}</span>
                                  <input
                                    type="text"
                                    value={val}
                                    onChange={(e) => handleVariableChange(currentVi, e.target.value)}
                                    maxLength={30}
                                    placeholder={varInfo.label || `Variable ${currentVi + 1}`}
                                    className="border-0 outline-none bg-transparent text-sm font-semibold px-1.5 py-0.5"
                                    style={{
                                      width: Math.max(90, Math.min(200, (val.length || 10) * 8 + 24)) + 'px',
                                      color: val ? '#059669' : '#9ca3af',
                                      caretColor: '#4f46e5',
                                    }}
                                  />
                                </span>
                              </span>
                            );
                            vi++;
                          }
                        });
                        return result;
                      })()}
                    </div>
                    {/* Paste toast notification */}
                    {pasteToast.show && (
                      <div className="absolute top-2 right-2 px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg animate-pulse z-10"
                        style={{
                          background: pasteToast.type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
                          color: 'white',
                        }}>
                        {pasteToast.type === 'success' ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
                        {pasteToast.message}
                      </div>
                    )}
                    {/* Hidden textarea for form validation */}
                    <textarea ref={textareaRef} value={editableMessage} onChange={(e) => setEditableMessage(e.target.value)}
                      rows={1}
                      className="sr-only"
                      tabIndex={-1}
                      aria-hidden="true" />
                  </div>
                  {validationErrors.message && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.message}</p>}
                  <div className="mt-2 flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                    <span className="text-gray-500">Chars: <strong className="text-gray-800">{stats.length}</strong></span>
                    <span className="w-px h-3 bg-gray-200"></span>
                    <span className="text-gray-500">Parts: <strong style={{ color: stats.smsCount > 1 ? '#d97706' : '#059669' }}>{stats.smsCount}</strong></span>
                    <span className="w-px h-3 bg-gray-200"></span>
                    <span className="text-gray-500">{encodingType}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dynamic Variables */}
          {variables.length > 0 && (
            <div className={cardBase + " p-5"} style={cardBorder}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                    <Hash className="w-3 h-3" style={{ color: '#d97706' }} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700">Dynamic Variables</h3>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#d97706' }}>{variables.length}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium text-gray-400 bg-gray-50 border border-gray-100">Max 30 chars each</span>
                </div>
                <button type="button" onClick={() => setShowVariables(!showVariables)}
                  className="text-xs font-medium px-3 py-1 rounded-lg transition-all" style={{ color: '#4f46e5', background: 'rgba(99, 102, 241, 0.06)' }}>
                  {showVariables ? 'Collapse' : 'Expand'}
                </button>
              </div>
              {showVariables && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {variables.map((varObj, index) => {
                      const tagColor = varObj.color || '#d97706';
                      return (
                        <div key={`var_${index}`} className="relative rounded-lg p-3" style={{ background: `${tagColor}06`, border: `1px solid ${tagColor}15` }}>
                          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1.5">
                            <span className="w-4 h-4 rounded flex items-center justify-center text-[10px] font-bold" style={{ background: `${tagColor}18`, color: tagColor }}>{index + 1}</span>
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: `${tagColor}15`, color: tagColor, border: `1px solid ${tagColor}25` }}>{varObj.tag}</span>
                          </label>
                          <div className="text-[10px] text-gray-400 mb-1.5">{varObj.label} — {varObj.description}</div>
                          <input type="text" value={variableValues[index] || ''} onChange={(e) => handleVariableChange(index, e.target.value)} maxLength={30}
                            className="w-full px-2.5 py-1.5 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 transition-all bg-white"
                            style={{ border: `1px solid ${tagColor}25` }}
                            placeholder={varObj.description || `Value ${index + 1}`} />
                          <span className="absolute right-4 bottom-[14px] text-[10px] text-gray-300">{variableValues[index]?.length || 0}/30</span>
                        </div>
                      );
                    })}
                  </div>
                  {validationErrors.variables && <p className="text-red-500 text-xs mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.variables}</p>}
                  {/* DLT Variable Tags Reference */}
                  <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">DLT Approved Variable Tags</p>
                    <div className="flex flex-wrap gap-1.5">
                      {DLT_VARIABLE_TAGS.map(t => (
                        <div key={t.tag} className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: `${t.color}08`, border: `1px solid ${t.color}15` }}>
                          <span className="text-[10px] font-mono font-bold" style={{ color: t.color }}>{t.tag}</span>
                          <span className="text-[9px] text-gray-400">— {t.description}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2 flex items-center gap-1"><Info className="w-3 h-3" /> All variable values are limited to <strong>30 characters</strong> maximum per DLT regulations.</p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Mobile Numbers */}
          {originalTemplate && (
            <div className={cardBase + " p-5"} style={cardBorder}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: 'rgba(6, 182, 212, 0.1)' }}>
                    <Phone className="w-3 h-3" style={{ color: '#0891b2' }} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700">Recipients</h3>
                </div>
                <div className="flex items-center gap-2">
                  {validNumbers.length > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(16, 185, 129, 0.08)', color: '#059669' }}><CheckCircle className="w-3 h-3" />{validNumbers.length}</span>}
                  {invalidNumbers.length > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(245, 158, 11, 0.08)', color: '#d97706' }}><AlertCircle className="w-3 h-3" />{invalidNumbers.length}</span>}
                </div>
              </div>
              <textarea value={mobileNumbers} onChange={handleMobileNumbersChange}
                onKeyPress={(e) => { const char = String.fromCharCode(e.which); if (!/[0-9\n,+,-]/.test(char) && e.which !== 13 && e.which !== 188 && e.which !== 8 && e.which !== 46 && e.which !== 36 && e.which !== 35) { e.preventDefault(); } }}
                rows={3}
                className="w-full px-3 py-2.5 font-mono text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 transition-all resize-none"
                style={{ border: validationErrors.mobile ? '1px solid #fca5a5' : validNumbers.length > 0 ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid rgba(99, 102, 241, 0.15)', background: validNumbers.length > 0 ? 'rgba(16, 185, 129, 0.02)' : 'white' }}
                placeholder="9876543210&#10;9123456789&#10;+919876543210" />
              {validationErrors.mobile && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.mobile}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="px-2 py-1 rounded text-xs text-gray-500" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>9876543210 · +919876543210 · 919876543210</span>
                <span className="px-2 py-1 rounded text-xs text-gray-500" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>Paste numbers up to 10,000 for best results</span>
              </div>
            </div>
          )}

          {/* Send Action Bar */}
          {originalTemplate && (
            <div className="rounded-xl p-3.5 flex items-center justify-end" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <div className="flex items-center gap-2">
                <button type="submit" disabled={loading}
                  className="px-5 py-2 rounded-lg text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  style={{ background: loading ? '#9ca3af' : 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  {loading ? (<><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/30" style={{ borderTopColor: 'white' }}></div>Sending...</>) : (<><Send className="w-3.5 h-3.5" />Send SMS</>)}
                </button>
                <button type="button" onClick={() => { setSelectedTemplate(''); setTemplateId(''); setTemplateName(''); setTemplateType(''); setOriginalTemplate(''); setEditableMessage(''); setVariables([]); setVariableValues([]); setMobileNumbers(''); setValidNumbers([]); setInvalidNumbers([]); setValidationErrors({}); setError(''); setSuccess(''); setPasteModal({ open: false, text: '', error: '' }); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-white transition-all" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>Reset</button>
              </div>
            </div>
          )}
        </form>

        {/* ──────── RIGHT SIDE (25%) — Sticky Sidebar ──────── */}
        <div className="hidden lg:block flex-shrink-0" style={{ width: '300px' }}>
          <div className="sticky top-4 space-y-4">

            {/* Validation Checklist */}
            <div className={cardBase + " p-4"} style={{ ...cardBorder, background: allReady ? 'rgba(16, 185, 129, 0.03)' : 'rgba(99, 102, 241, 0.03)' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: allReady ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)' }}>
                  <Shield className="w-3 h-3" style={{ color: allReady ? '#059669' : '#6366f1' }} />
                </div>
                <h3 className="text-sm font-semibold text-gray-700">Readiness</h3>
                <span className="ml-auto text-sm font-bold" style={{ color: allReady ? '#059669' : '#6366f1' }}>{readyCount}/{validationChecks.length}</span>
              </div>
              {/* Progress bar */}
              <div className="w-full h-1.5 rounded-full mb-3" style={{ background: 'rgba(99, 102, 241, 0.08)' }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(readyCount / validationChecks.length) * 100}%`, background: allReady ? 'linear-gradient(90deg, #059669, #34d399)' : 'linear-gradient(90deg, #4f46e5, #06b6d4)' }}></div>
              </div>
              <div className="space-y-1.5">
                {validationChecks.map(check => (
                  <div key={check.key} className="flex items-center gap-2">
                    {check.done ? (
                      <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#059669' }} />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ border: '1.5px solid #d1d5db' }}></div>
                    )}
                    <span className={`text-sm ${check.done ? 'text-gray-700' : 'text-gray-400'}`}>{check.label}</span>
                  </div>
                ))}
              </div>
              {allReady && (
                <div className="mt-3 px-2.5 py-2 rounded-lg text-sm font-semibold text-center" style={{ background: 'rgba(16, 185, 129, 0.08)', color: '#059669' }}>
                  Ready to send!
                </div>
              )}
            </div>

            {/* Approved Template — Read Only with Metadata */}
            {originalTemplate && (
              <div className={cardBase + " p-4"} style={cardBorder}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: 'rgba(99, 102, 241, 0.1)' }}>
                    <FileText className="w-3 h-3" style={{ color: '#6366f1' }} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700">Approved Template</h3>
                  <span className="ml-auto px-2 py-0.5 rounded text-xs font-medium" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>Read Only</span>
                </div>
                {/* Template Metadata Grid */}
                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Selected Header</span>
                    <span className="text-xs font-bold text-gray-800">{headers.find(h => h.id.toString() === selectedHeader)?.header_name || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Name</span>
                    <span className="text-xs font-bold text-gray-800">{templateName || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Template ID</span>
                    <span className="text-xs font-bold text-gray-800 font-mono">{templateId || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Template Type</span>
                    <span className="text-xs font-bold text-gray-800">{templateType || '-'}</span>
                  </div>
                  {variables.length > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Variables</span>
                      <span className="text-xs font-bold" style={{ color: '#dc2626' }}>{variables.length} var{variables.length > 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
                {/* Full Template Message — No Scroll */}
                <div className="pt-2" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <p className="text-xs text-gray-500 mb-1.5">Template Content</p>
                  <div className="px-2.5 py-2 rounded-lg whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700"
                    style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                    {originalTemplate.split(DLT_VAR_REGEX).map((part, index) => {
                      const tagMatches = originalTemplate.match(DLT_VAR_REGEX) || [];
                      const elements = [<span key={`t-${index}`}>{part}</span>];
                      if (index < tagMatches.length) {
                        const tag = tagMatches[index];
                        const tagInfo = DLT_VARIABLE_TAGS.find(t => t.tag === tag) || DLT_VARIABLE_TAGS[0];
                        elements.push(<span key={`v-${index}`} className="font-semibold px-0.5 rounded" style={{ background: `${tagInfo.color}15`, color: tagInfo.color }}>{tag}</span>);
                      }
                      return elements;
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* SMS Summary */}
            {originalTemplate && editableMessage && (
              <div className={cardBase + " p-4"} style={{ ...cardBorder, background: 'rgba(6, 182, 212, 0.03)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: 'rgba(6, 182, 212, 0.1)' }}>
                    <Zap className="w-3 h-3" style={{ color: '#0891b2' }} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700">SMS Summary</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Characters</span>
                    <span className="text-xs font-bold text-gray-800">{stats.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">SMS Parts</span>
                    <span className="text-xs font-bold" style={{ color: stats.smsCount > 1 ? '#d97706' : '#059669' }}>{stats.smsCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Encoding</span>
                    <span className="text-xs font-bold text-gray-800">{encodingType}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Recipients</span>
                    <span className="text-xs font-bold" style={{ color: validNumbers.length > 0 ? '#059669' : '#9ca3af' }}>{validNumbers.length}</span>
                  </div>
                  <div className="pt-2 mt-1" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700">Total SMS</span>
                      <span className="text-sm font-bold" style={{ color: '#4f46e5' }}>{stats.smsCount * (validNumbers.length || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Tips */}
            <div className={cardBase + " p-4"} style={{ ...cardBorder, background: 'rgba(245, 158, 11, 0.03)' }}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                  <Eye className="w-3 h-3" style={{ color: '#d97706' }} />
                </div>
                <h3 className="text-sm font-semibold text-gray-700">Quick Tips</h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-sm mt-0.5" style={{ color: '#6366f1' }}>•</span>
                  <p className="text-sm text-gray-500 leading-relaxed">Use the <strong>Paste Message</strong> box to auto-fill variables from a complete message.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-sm mt-0.5" style={{ color: '#6366f1' }}>•</span>
                  <p className="text-sm text-gray-500 leading-relaxed">Numbers are <strong>auto-deduplicated</strong> — duplicates removed automatically.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-sm mt-0.5" style={{ color: '#6366f1' }}>•</span>
                  <p className="text-sm text-gray-500 leading-relaxed">Use <strong>Unicode</strong> for Hindi, Gujarati, or regional languages.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-sm mt-0.5" style={{ color: '#6366f1' }}>•</span>
                  <p className="text-sm text-gray-500 leading-relaxed">Max <strong>30 characters</strong> per variable value.</p>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>{/* end 75/25 split */}
    </div>
  );
};

export default SendSMS;
